// src/components/payment/PaymentTransactionList.js
import React, { useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
  Stack,
  Tooltip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EvStationIcon from '@mui/icons-material/EvStation';
import { format } from 'date-fns';
import LoadingSpinner from '../common/Loadingspinner';
import ErrorAlert from '../common/ErrorAlert';
import { getChargers } from '../../api/chargerAPI';
import { getDrivers } from '../../api/driverAPI';
import { getCompanies } from '../../api/companyAPI';
import { getSites } from '../../api/siteAPI';

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
  } catch {
    return dateString;
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'succeeded':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    case 'canceled':
    case 'cancelled':
      return 'default';
    case 'refunded':
      return 'info';
    default:
      return 'default';
  }
};

const getPaymentStatusColor = (paymentStatus) => {
  switch (paymentStatus?.toLowerCase()) {
    case 'completed':
    case 'succeeded':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    case 'canceled':
    case 'cancelled':
      return 'default';
    case 'refunded':
      return 'info';
    default:
      return 'default';
  }
};

// Helper function to determine if payment button should be shown
const shouldShowPaymentButton = (transaction) => {
  // Show payment button if payment status is pending or failed and amount is not 0
  const paymentStatus = transaction.PaymentTransactionPaymentStatus?.toLowerCase();
  const amount = transaction.PaymentTransactionAmount || 0;
  return paymentStatus && ['pending', 'failed'].includes(paymentStatus) && amount > 0;
};

const PaymentTransactionList = ({ 
  paymentTransactions, 
  isLoading, 
  isError, 
  error 
}) => {
  // Fetch all data for lookups
  const { data: allChargers } = useQuery({
    queryKey: ['all-chargers'],
    queryFn: () => getChargers({})
  });

  const { data: allDrivers } = useQuery({
    queryKey: ['all-drivers'],
    queryFn: () => getDrivers()
  });

  const { data: allCompanies } = useQuery({
    queryKey: ['all-companies'],
    queryFn: () => getCompanies()
  });

  const { data: allSites } = useQuery({
    queryKey: ['all-sites'],
    queryFn: () => getSites()
  });

  // Create lookup maps for ID -> name conversion
  const chargerLookup = useMemo(() => {
    if (!allChargers) return {};
    const lookup = {};
    allChargers.forEach(charger => {
      lookup[charger.ChargerId] = charger.ChargerName;
    });
    return lookup;
  }, [allChargers]);

  const driverLookup = useMemo(() => {
    if (!allDrivers) return {};
    const lookup = {};
    allDrivers.forEach(driver => {
      lookup[driver.DriverId] = {
        name: driver.DriverFullName,
        email: driver.DriverEmail
      };
    });
    return lookup;
  }, [allDrivers]);

  const companyLookup = useMemo(() => {
    if (!allCompanies) return {};
    const lookup = {};
    allCompanies.forEach(company => {
      lookup[company.CompanyId] = company.CompanyName;
    });
    return lookup;
  }, [allCompanies]);

  const siteLookup = useMemo(() => {
    if (!allSites) return {};
    const lookup = {};
    allSites.forEach(site => {
      lookup[site.SiteId] = site.SiteName;
    });
    return lookup;
  }, [allSites]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading payment transactions'} />;
  }

  if (!paymentTransactions || paymentTransactions.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          No payment transactions found
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent sx={{ padding: 0 }}>
        <TableContainer component={Paper} sx={{ maxHeight: 650, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="120px">Transaction ID</TableCell>
                <TableCell width="120px">Session ID</TableCell>
                <TableCell width="180px">Driver Name</TableCell>
                <TableCell width="180px">Company Name</TableCell>
                <TableCell width="220px">Site Name</TableCell>
                <TableCell width="180px">Charger Name</TableCell>
                <TableCell width="100px">Amount</TableCell>
                <TableCell width="180px">Date & Time</TableCell>
                <TableCell width="140px">Payment Status</TableCell>
                <TableCell width="140px" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentTransactions.map((transaction) => {
                // Get names using lookup maps
                const driverData = transaction.PaymentTransactionDriverId ? 
                  (driverLookup[transaction.PaymentTransactionDriverId] || { name: `Driver ${transaction.PaymentTransactionDriverId}`, email: null }) : 
                  null;
                const companyName = companyLookup[transaction.PaymentTransactionCompanyId] || `Company ${transaction.PaymentTransactionCompanyId}`;
                const siteName = siteLookup[transaction.PaymentTransactionSiteId] || `Site ${transaction.PaymentTransactionSiteId}`;
                const chargerName = chargerLookup[transaction.PaymentTransactionChargerId] || `Charger ${transaction.PaymentTransactionChargerId}`;

                return (
                  <TableRow key={transaction.PaymentTransactionId}>
                    <TableCell>#{transaction.PaymentTransactionId}</TableCell>
                    
                    <TableCell>
                      {transaction.PaymentTransactionSessionId ? (
                        <Chip 
                          label={`#${transaction.PaymentTransactionSessionId}`} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    
                    <TableCell>
                      {transaction.PaymentTransactionDriverId ? (
                        <Box display="flex" alignItems="flex-start">
                          <PersonIcon fontSize="small" sx={{ mr: 0.5, mt: 0.25 }} />
                          <Box>
                            <Typography variant="body2" noWrap>
                              {driverData.name}
                            </Typography>
                            {driverData.email && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {driverData.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2" noWrap>
                          {companyName}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2" noWrap>
                          {siteName}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <EvStationIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2" noWrap>
                          {chargerName}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        ${transaction.PaymentTransactionAmount?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      {formatDateTime(transaction.PaymentTransactionDateTime)}
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={transaction.PaymentTransactionPaymentStatus || 'Unknown'} 
                        color={getPaymentStatusColor(transaction.PaymentTransactionPaymentStatus)}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Transaction Details">
                          <IconButton 
                            component={Link} 
                            to={`/payment/transactions/${transaction.PaymentTransactionId}`}
                            color="primary"
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {shouldShowPaymentButton(transaction) && transaction.PaymentTransactionSessionId && (
                          <Tooltip title="Make Payment">
                            <IconButton 
                              component={Link} 
                              to={`/sessions/${transaction.PaymentTransactionSessionId}/payment`}
                              color="success"
                              size="small"
                              sx={{
                                bgcolor: 'success.main',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: 'success.dark',
                                }
                              }}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default PaymentTransactionList;